import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Alert } from 'antd';
import { captureAnalytics } from '../../../actions/preload';
import { csrfToken } from '../helpers';
import useLatestRequest from '../useLatestRequest';
import LoadingAnimation from '../LoadingAnimation';
import UserActivityFilters from './UserActivityFilters';
import SummaryCards, { showArmWork } from './SummaryCards';
import WorkOverTime from './WorkOverTime';
import ArrivalHeatmap from './ArrivalHeatmap';
import InboxActivity from './InboxActivity';
import TeamActivity from './TeamActivity';
import {
  MAX_DATE_RANGE_DAYS,
  dateRangeDays,
  dateRangeExceedsMax,
  emptyFilters,
  formatRangeLabel,
  userActivityParams,
} from './helpers';
import './userActivity.scss';

// User Activity tab (ENG-3857): what the team, ARM and configured automations
// got done in the selected dates. Admin-only; gated by the Analytics container.
const UserActivityDashboard = ({ active, filtersContainerId }) => {
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [inbox, setInbox] = useState(null);
  const [team, setTeam] = useState(null);
  const [error, setError] = useState(null);
  const [draftFilters, setDraftFilters] = useState(emptyFilters());
  const [filtersTarget, setFiltersTarget] = useState(null);
  const runLatestSummaryRequest = useLatestRequest();
  const runLatestInboxRequest = useLatestRequest();
  const runLatestTeamRequest = useLatestRequest();

  const requestSection = (section, filters) => () => axios({
    method: 'GET',
    url: `/api/v1/user_activity_analytics/${section}`,
    params: userActivityParams(filters),
    headers: { 'X-CSRF-Token': csrfToken() },
  });

  const reportError = (requestError) => {
    console.error(requestError);
    setError(requestError.response?.data?.error || 'Unable to load user activity.');
  };

  // Each section loads on its own so a slow one never holds up the rest.
  const fetchSections = (filters) => {
    setLoading(true);
    setError(null);
    runLatestInboxRequest({
      request: requestSection('inbox', filters),
      onResolve: (response) => setInbox(response.data),
      onReject: reportError,
    });
    runLatestTeamRequest({
      request: requestSection('team', filters),
      onResolve: (response) => setTeam(response.data),
      onReject: reportError,
    });
    runLatestSummaryRequest({
      request: requestSection('summary', filters),
      onResolve: (response) => {
        setSummary(response.data);
        // The server fills in the default range; show the dates it used. Keep
        // the current draft so edits made while this request ran survive.
        setDraftFilters((current) => (current.dateRange.startDate ? current : {
          ...current,
          dateRange: { startDate: response.data.date_range.start_date, endDate: response.data.date_range.end_date },
        }));
      },
      onReject: reportError,
      onFinally: () => setLoading(false),
    });
  };

  useEffect(() => {
    if (active && !summary) fetchSections(draftFilters);
  }, [active]);

  useEffect(() => {
    setFiltersTarget(active ? document.getElementById(filtersContainerId) : null);
  }, [active, filtersContainerId]);

  const applyFilters = () => {
    if (dateRangeExceedsMax(draftFilters.dateRange)) {
      setError(`Date range cannot exceed ${MAX_DATE_RANGE_DAYS} days`);
      return;
    }

    captureAnalytics('user_activity_filters_applied', {
      department_count: draftFilters.departmentIds.length,
      user_count: draftFilters.userIds.length,
      date_range_days: dateRangeDays(draftFilters.dateRange),
    });
    fetchSections(draftFilters);
  };

  const clearFilters = () => {
    const filters = emptyFilters();
    captureAnalytics('user_activity_filters_cleared');
    setDraftFilters(filters);
    fetchSections(filters);
  };

  const filters = (
    <UserActivityFilters
      filters={draftFilters}
      onChange={(change) => setDraftFilters((current) => ({ ...current, ...change }))}
      filterOptions={summary?.filter_options}
      loading={loading}
      onApply={applyFilters}
      onClear={clearFilters}
    />
  );

  return (
    <div className="user-activity-dashboard">
      {filtersTarget && createPortal(filters, filtersTarget)}
      {error && <Alert type="error" showIcon message={error} />}
      {!summary && loading && <LoadingAnimation />}
      {summary && (
        <>
          <div className="user-activity-range-strip">
            <strong>{formatRangeLabel({ startDate: summary.date_range.start_date, endDate: summary.date_range.end_date })}</strong>
            {loading && <span className="user-activity-caption">Updating…</span>}
          </div>
          <SummaryCards data={summary} />
          <div className="user-activity-trends">
            <WorkOverTime weeks={summary.work_over_time} showArm={showArmWork(summary)} />
            <ArrivalHeatmap arrivals={summary.arrivals} />
          </div>
        </>
      )}
      {inbox && <InboxActivity inbox={inbox} />}
      {team && <TeamActivity team={team} />}
    </div>
  );
};

UserActivityDashboard.propTypes = {
  active: PropTypes.bool.isRequired,
  filtersContainerId: PropTypes.string,
};

UserActivityDashboard.defaultProps = {
  filtersContainerId: 'user-activity-analytics-filters',
};

export default UserActivityDashboard;
